import { StorageService } from '../../services/storage/storage.service';

const TASK_KEY = 'personal-task-search-updates';
const DIARY_KEY = 'client-diary-search-updates';

export interface TaskSearchUpdate {
  taskUid: number;
  title?: string;
  dueDate?: Date | string;
  message?: string;
  status?: string;
  sendEmail?: boolean;
}

export interface DiarySearchUpdate {
  id: number;
  dueDate?: Date | string;
  message?: string;
  allowOthers?: boolean;
  emailReminder?: boolean;
  isCompleted?: boolean;
  diaryTypeId?: number | string;
  followUpUser?: string;
  additionalUsers?: { userID: string }[];
  clientNumber?: number | string | null;
}

export function rememberTaskSearchUpdate(
  storage: StorageService,
  update: TaskSearchUpdate,
): void {
  remember(storage, TASK_KEY, 'taskUid', update.taskUid, update);
}

export function findTaskSearchUpdate(
  storage: StorageService,
  taskUid: number,
): TaskSearchUpdate | null {
  return find(storage, TASK_KEY, 'taskUid', taskUid);
}

export function clearTaskSearchUpdates(storage: StorageService): void {
  storage.remove(TASK_KEY);
}

export function rememberDiarySearchUpdate(
  storage: StorageService,
  update: DiarySearchUpdate,
): void {
  remember(storage, DIARY_KEY, 'id', update.id, update);
}

export function findDiarySearchUpdate(
  storage: StorageService,
  id: number,
): DiarySearchUpdate | null {
  return find(storage, DIARY_KEY, 'id', id);
}

export function clearDiarySearchUpdates(storage: StorageService): void {
  storage.remove(DIARY_KEY);
}

function remember<T extends object>(
  storage: StorageService,
  key: string,
  idField: keyof T,
  id: number,
  update: T,
): void {
  const list = readList<T>(storage, key);
  const existing: Partial<T> =
    list.find((item) => Number(item[idField]) === id) ?? {};
  const next = list.filter((item) => Number(item[idField]) !== id);
  next.push({ ...existing, ...update });
  storage.set(key, next);
}

function find<T extends object>(
  storage: StorageService,
  key: string,
  idField: keyof T,
  id: number,
): T | null {
  const list = readList<T>(storage, key);
  return list.find((item) => Number(item[idField]) === id) ?? null;
}

function readList<T>(storage: StorageService, key: string): T[] {
  const value: unknown = storage.get(key);
  return Array.isArray(value) ? (value as T[]) : [];
}
